import { useMemo } from "react";
import { PieChart, Pie, Cell } from "recharts";
import { Users, Smartphone, MessageSquare, DollarSign, WifiOff, Users2, HeartPulse } from "lucide-react";

export default function AdminMetrics({ metrics }) {
  const behaviorSub = useMemo(() => {
    const b = (metrics && metrics.behaviors_today) || {};
    const parts = [];
    if (b.resposta_citada) parts.push(`${b.resposta_citada} citadas`);
    if (b.reacao) parts.push(`${b.reacao} reações`);
    if (b.enquete) parts.push(`${b.enquete} enquetes`);
    return parts.length > 0 ? parts.join(" · ") : "todos os usuários";
  }, [metrics]);
  const cards = useMemo(
    () =>
      metrics
        ? [
            { icon: Users, label: "Clientes Cadastrados", value: metrics.total_users, sub: `${metrics.active_users} ativos`, color: "text-blue-400", testid: "admin-metric-users" },
            { icon: Smartphone, label: "Chips Conectados", value: metrics.total_chips, sub: "toda a plataforma", color: "text-emerald-400", testid: "admin-metric-chips" },
            { icon: WifiOff, label: "Chips Desconectados", value: metrics.disconnected_chips, sub: "sessão Evolution caiu", color: metrics.disconnected_chips > 0 ? "text-red-400" : "text-slate-500", testid: "admin-metric-disconnected" },
            { icon: Users2, label: "Grupos Ativos", value: metrics.grupos_ativos, sub: "grupos controlados", color: "text-cyan-400", testid: "admin-metric-grupos" },
            { icon: MessageSquare, label: "Mensagens Hoje", value: metrics.messages_today.toLocaleString("pt-BR"), sub: behaviorSub, color: "text-purple-400", testid: "admin-metric-messages" },
            { icon: DollarSign, label: "MRR Estimado", value: `R$ ${metrics.mrr.toLocaleString("pt-BR")}`, sub: "receita mensal recorrente", color: "text-amber-400", testid: "admin-metric-mrr" },
          ]
        : [],
    [metrics]
  );

  const healthData = useMemo(
    () => (metrics ? [{ v: metrics.avg_health }, { v: 100 - metrics.avg_health }] : []),
    [metrics]
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="admin-metrics-container">
        {cards.map((c) => (
          <div key={c.label} className="glass-card glass-card-hover p-5" data-testid={c.testid}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{c.label}</span>
              <c.icon className={`w-5 h-5 ${c.color}`} />
            </div>
            <p className={`text-2xl font-extrabold tracking-tight font-mono ${c.color}`}>{c.value}</p>
            <p className="text-[11px] text-slate-500 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      {metrics && (
        <div className="glass-card p-5 sm:p-6 flex items-center gap-6" data-testid="admin-health-donut">
          <div className="w-28 h-28 shrink-0">
            <PieChart width={112} height={112}>
              <Pie data={healthData}
                dataKey="v" innerRadius={34} outerRadius={52} startAngle={90} endAngle={-270} strokeWidth={0}>
                <Cell fill={metrics.avg_health >= 80 ? "#10B981" : metrics.avg_health >= 50 ? "#F59E0B" : "#EF4444"} />
                <Cell fill="rgba(255,255,255,0.06)" />
              </Pie>
            </PieChart>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <HeartPulse className="w-4 h-4 text-emerald-400" /> Saúde consolidada da plataforma
            </p>
            <p className={`text-3xl font-extrabold font-mono mt-1 ${metrics.avg_health >= 80 ? "text-emerald-400" : metrics.avg_health >= 50 ? "text-amber-400" : "text-red-400"}`}>
              {metrics.avg_health}%
            </p>
            <p className="text-xs text-slate-500 mt-1">Média de saúde de todos os chips (erros dos últimos 7 dias). {metrics.errors_total} erro(s) acumulados no total.</p>
          </div>
        </div>
      )}
    </>
  );
}
